import { Subtitle } from "@/app/(app)/(private)/video-subtitles/UploadFlow/types";
import { FormattedDateTime } from "@/types/common";

export const hexToRgbArray = (hex: string) => {
  // Remove # if present and expand shorthand
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }

  // Validate length
  if (hex.length !== 6) {
    throw new Error("Invalid hex color format");
  }

  // Convert to RGB array of strings
  const num = parseInt(hex, 16);
  return [
    ((num >> 16) & 255).toString(),
    ((num >> 8) & 255).toString(),
    (num & 255).toString(),
  ];
};

export const rgbToHex = (rgb: string[]): string => {
  const [r, g, b] = rgb.map((num) => parseInt(num));
  return `#${[r, g, b]
    .map((x) => {
      const hex = x.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    })
    .join("")}`;
};

export const forceDownload = (url: string, filename = "downloaded-file") => {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;

  // Required for cross-origin downloads in some browsers
  anchor.target = "_blank";

  // Append and trigger
  document.body.appendChild(anchor);
  anchor.click();

  // Clean up
  document.body.removeChild(anchor);
};

export function formatReadableDateTime(isoString: string): FormattedDateTime {
  /**
   * Converts an ISO 8601 UTC datetime string to a readable date and time.
   * @param isoString ISO format: e.g., "2025-05-06T10:05:40.000000Z"
   */

  const date = new Date(isoString); // Automatically parses the ISO string

  const dateString = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);

  const timeString = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(date);

  return { date: dateString, time: timeString };
}

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export function generateSubtitleJsonMeta(
  sequenceSubtitles: Subtitle[],
  wordSubtitles: Subtitle[]
) {
  if (!Array.isArray(sequenceSubtitles) || !Array.isArray(wordSubtitles)) {
    return [];
  }

  return sequenceSubtitles.map((seq) => {
    const [startStr, endStr] = seq?.time?.split(" - ");
    const start = parseFloat(startStr);
    const end = parseFloat(endStr);

    const words = wordSubtitles
      .map((word) => {
        const [wStartStr, wEndStr] = word?.time?.split(" - ");
        const wStart = parseFloat(wStartStr);
        const wEnd = parseFloat(wEndStr);

        if (wStart >= start && wEnd <= end) {
          return {
            word: word?.title,
            start: wStart,
            end: wEnd,
          };
        }

        return null;
      })
      .filter(Boolean); // remove nulls

    return {
      sequences: seq?.title?.trim(),
      start,
      end,
      words,
    };
  });
}

export const getStrokeWidth = (
  size: "none" | "small" | "medium" | "large"
): string => {
  switch (size) {
    case "small":
      return "0.1em";
    case "medium":
      return "0.15em";
    case "large":
      return "0.2em";
    case "none":
    default:
      return "0";
  }
};

export const getShadowOffset = (
  size: "none" | "small" | "medium" | "large"
): string => {
  switch (size) {
    case "small":
      return "0.1em 0.1em 0.1em";
    case "medium":
      return "0.15em 0.15em 0.15em";
    case "large":
      return "0.2em 0.2em 0.2em";
    case "none":
    default:
      return "0";
  }
};

export function groupWordsByCount(wordCaptions: any, how: any) {
  const structured = [];

  for (let i = 0; i < wordCaptions.length; i += how) {
    const chunk = wordCaptions.slice(i, i + how);

    const text = chunk.map((item: any) => item.word).join(" ");
    const start = chunk[0].start;
    const end = chunk[chunk.length - 1].end;

    // Build sentences array with individual word data
    const sentences = chunk.map((item: any) => ({
      word: item?.word,
      start: item?.start,
      end: item?.end,
    }));

    structured.push({
      text,
      start,
      end,
      sentences,
    });
  }

  return structured;
}

export function groupWords(words: any, groupSize: any) {
  const grouped = [];

  for (let i = 0; i < words.length; i += groupSize) {
    grouped.push(words.slice(i, i + groupSize));
  }

  return grouped;
}

export const isMobileDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  return /iPhone|iPad|iPod|Android|Mobile/i.test(window.navigator.userAgent);
};
